import { Component, Injector, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { GridComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { GridBaseComponent } from '../../../../../components/common/grid-base/grid-base';
import { PolluxGridConfig } from '../../../../../components/common/pollux-grid-config/pollux-grid-config';
import { WorkspaceBase } from '../../../../../components/common/workspace-base/workspace-base';
import { ValuationsWorkspaceComponent } from '../../workspaces/valuations-workspace/valuations-workspace.component';
import { valuationsGridColumns } from './valuations-grid-config';

/**
 * Projects grid
 */
@Component({
  selector: 'valuations-grid',
  templateUrl: './valuations-grid.component.html',
  styleUrls: ['./valuations-grid.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ValuationsGridComponent extends GridBaseComponent implements OnInit {
  /**
   * A reference to the Kendo grid component
   */
  @ViewChild(GridComponent) kendoGrid: GridComponent;

  /**
   * A reference to the workspace
   */
  public get workspaceReference(): WorkspaceBase {
    return this.workspace;
  }

  /**
   * Gets a list of columns
   * @returns
   */
  public getColumns(): PolluxGridConfig[] {
    return valuationsGridColumns;
  }

  /**
   * The workspace
   */
  @Input() workspace: ValuationsWorkspaceComponent;

  /**
   * Constructor
   * @param injector The Angular injector
   */
  constructor(protected injector: Injector) {
    super(injector, 'valuation', false);
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.init();
  }

  /**
   * Handle a row selection / deselection event
   * @param _event The event. Can be null!
   * @param _item The item. If an item was deselected, it's null
   */
  protected handleRowSelection(_event: any, _item: any): void {
    //nothing
  }

  /**
   * Handles if a cell was clicked
   * @param _event The event. Can be null!
   */
  protected handleCellClick(_event: any): void {
    //nothing
  }

  /**
   * Handle a doubleclick event
   * @param _event The event. Can be null!
   * @param _item The item
   */
  protected handleDoubleClick(_event: any, _item: any): void {
    //nothing
  }

  /**
   * Gets the url for loading a paged list
   * If server paging is not supported return null and override getDataUrl
   * @returns The url for loading a paged list
   */
  protected getPagedListEndpoint(): string {
    return null;
  }

  /**
   * Gets the grid data URL with query string. Preferrably, use getPagedListEndpoint and only use this method if absolutely necessary.
   * @returns
   */
  protected getGridDataUrlWithQueryString(): string {
    const parentReportId = this.workspace.parentReportId;
    if (parentReportId > 0) {
      const includeZero = false;
      const sortBy = 'ImportDate';
      const sortDirection = 'DESC';

      const baseUrl = this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `project/valuation/valuation/filter`);
      const dataUrl = `${baseUrl}?parentReportId=${parentReportId}&includeZero=${includeZero}&sortBy=${sortBy}&sortDirection=${sortDirection}`;
      return dataUrl;
    }

    return null;
  }

  /**
   * Reset the filters and show all items
   */
  public showAll(): void {
    super.showAll();
    this.workspace.onShowAll();
  }

  /**
   * Data loading callback
   * @param data The loaded data
   */
  protected onDataLoaded(data: GridDataResult): void {
    this.workspace.setCounterValue('valuation', data.total);
  }

  /**
   * Registers any required events. Please don't forget to unsubscibe!
   */
  protected registerEvents(): void {
    this.onEvents.push(this.eventBus.subscribeEvent('ValuationLoadData', (_payload, _options) => {
      this.showAll();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('valuationProjectReportItemSaved', (_payload, _options) => {
      this.showAll();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('ValuationRefreshData', (_payload, _options) => {
      this.showAll();
    }));
  }
}
